/**
 * Session portail — jeton réel (localStorage, partagé entre onglets) ou jeton « naviguer en tant que » (sessionStorage,
 * propre à l'onglet : deux onglets peuvent viser deux clients différents sans se contaminer) — Batch #14.338.
 *
 * Copie mémoire : Safari (iOS) peut perdre une écriture sessionStorage faite juste avant une navigation et n'écrit pas
 * toujours de façon synchrone → le jeton est aussi gardé en mémoire pour la durée de l'onglet, et l'installation
 * ne provoque plus de rechargement de page (navigation React Router).
 */
import axios from "axios";
import { toast } from "@/lib/toast";

const IMP_TOKEN = "impersonationToken";
const IMP_INFO = "impersonation";
const HANDOFF_PREFIX = "impHandoff:";
const mem = { token: "", info: null };

const ss = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* stockage indisponible : la mémoire suffit pour l'onglet */ } },
  del: (k) => { try { sessionStorage.removeItem(k); } catch { /* noop */ } },
};

export const getPortalToken = () => {
  if (typeof window === "undefined") return "";
  return mem.token || ss.get(IMP_TOKEN) || localStorage.getItem("portalToken") || "";
};
export const isImpersonating = () => typeof window !== "undefined" && !!(mem.token || ss.get(IMP_TOKEN));
export const getImpersonation = () => {
  if (mem.info) return mem.info;
  try { return JSON.parse(ss.get(IMP_INFO) || "null"); } catch { return null; }
};

/** Onglet admin : dépose le jeton pour l'onglet portail qui va s'ouvrir (clé à usage unique, 2 min). */
export const putHandoff = (data) => {
  const key = Math.random().toString(36).slice(2, 10);
  localStorage.setItem(HANDOFF_PREFIX + key, JSON.stringify({ ...data, put_at: Date.now() }));
  return key;
};
/** Installe le jeton « en tant que » pour CET onglet (mémoire + sessionStorage). */
export const installImpersonation = (d) => {
  mem.token = d.access_token;
  mem.info = { client: d.client, mode: d.mode, expires_at: d.expires_at, session_id: d.session_id };
  ss.set(IMP_TOKEN, mem.token);
  ss.set(IMP_INFO, JSON.stringify(mem.info));
};
/** Onglet portail : récupère (et détruit) le jeton déposé, puis l'installe. */
export const takeHandoff = (key) => {
  const raw = localStorage.getItem(HANDOFF_PREFIX + key);
  if (!raw) return null;
  localStorage.removeItem(HANDOFF_PREFIX + key);
  try {
    const d = JSON.parse(raw);
    if (Date.now() - (d.put_at || 0) > 2 * 60 * 1000) return null;
    installImpersonation(d);
    return d;
  } catch { return null; }
};
export const clearImpersonation = () => {
  mem.token = ""; mem.info = null;
  ss.del(IMP_TOKEN);
  ss.del(IMP_INFO);
};

// Toast unique quand le serveur refuse une écriture en mode « en tant que » (toutes les instances axios du portail)
let installed = false;
export const installImpersonationInterceptor = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(undefined, (error) => {
    const d = error?.response?.data;
    if (error?.response?.status === 403 && d?.impersonation_blocked) {
      toast.error(d.detail || "Mode admin : action bloquée", { id: "impersonation-blocked", duration: 5000 });
    }
    return Promise.reject(error);
  });
};
